import {
  Node as FlowNode,
  Edge as FlowEdge,
  Connection,
  MarkerType
} from '@xyflow/react'
import NodeType from 'types/transformation/enums/NodeType'

import { BOARD } from 'components_new/molecules/Node/accents'

import {
  AggregateNode,
  CalculateNode,
  CastNode,
  ClearNode,
  ConcatenateNode,
  DateParserNode,
  FilterNode,
  InputNode,
  LeftJoinNode,
  LowerNode,
  Node,
  NodeData,
  OneChildNode,
  ReplaceNode,
  TextReplaceNode,
  TimeDifferenceNode,
  TrimNode,
  TwoChildNode,
  UniteNode
} from 'types/transformation/types/nodes'

import DataType from 'types/transformation/enums/DataType'

import * as Calculate from '../Node/Calculate/utils'
import * as Concatenate from '../Node/Concatenate/utils'
import * as Unite from '../Node/Unite/utils'

/**
 * Find first available index, starting from 0
 */
function findNextIndex(nodes: Node[]) {
  const used = new Set<number>(nodes.map((item) => item.data.index))

  // find first available number
  let next = 0

  while (used.has(next)) {
    next += 1
  }

  return next
}

export function getInitNode(type: NodeType, nodes: Node[]): Node['data'] {
  const index = findNextIndex(nodes)
  const baseNode = {
    id: `${index}_${type}`,
    type: type,
    location_x: 100,
    location_y: 100,
    index: index
  }

  switch (type) {
  case NodeType.AGGREGATE: {
    const node: AggregateNode = {
      ...baseNode,
      left_node: null,
      group_by: null,
      schema: []
    }

    return node
  }
  case NodeType.CALCULATE: {
    const node: CalculateNode = {
      ...baseNode,
      expression: '',
      left_node: null,
      new_column: Calculate.getNewColumn(baseNode)
    }

    return node
  }
  case NodeType.CAST: {
    const node: CastNode = {
      ...baseNode,
      left_node: null,
      change_column: null
    }

    return node
  }
  case NodeType.CLEAR: {
    const node: ClearNode = {
      ...baseNode,
      left_node: null,
      columns: []
    }

    return node
  }
  case NodeType.FILTER: {
    const node: FilterNode = {
      ...baseNode,
      left_node: null,
      filter_conditions: []
    }

    return node
  }

  case NodeType.INPUT: {
    const node: InputNode = {
      ...baseNode,
      source_table_id: null
    }

    return node
  }
  case NodeType.LOWER: {
    const node: LowerNode = {
      ...baseNode,
      left_node: null,
      left_column: null
    }

    return node
  }
  case NodeType.LEFT_JOIN: {
    const node: LeftJoinNode = {
      ...baseNode,
      left_node: null,
      right_node: null,
      left_column: null,
      right_column: null
    }

    return node
  }
  case NodeType.CONCATENATE: {
    const node: ConcatenateNode = {
      ...baseNode,
      left_node: null,
      columns: []
    }

    node.columns.push(Concatenate.getNewColumn(node))

    return node
  }
  case NodeType.DATE_PARSER: {
    const node: DateParserNode = {
      ...baseNode,
      left_node: null,
      left_column: null,
      format: '',
      new_column: {
        id: `${baseNode.id}_new_column`,
        name: '',
        reference_node: baseNode.id,
        reference_node_index: baseNode.index,
        type: DataType.DATE
      }
    }

    return node
  }
  case NodeType.REPLACE: {
    const node: ReplaceNode = {
      ...baseNode,
      left_node: null,
      left_column: null,
      replace_column: null,
      replace_value: null,
      new_column: null,
      filter_conditions: []
    }

    return node
  }
  case NodeType.TIME_DIFFERENCE: {
    const node: TimeDifferenceNode = {
      ...baseNode,
      left_node: null,
      left_column: null,
      right_column: null,
      value: null,
      unit: null,
      new_column: {
        id: `${baseNode.id}_new_column`,
        name: '',
        reference_node: baseNode.id,
        reference_node_index: baseNode.index,
        type: DataType.BIGINT
      }
    }

    return node
  }
  case NodeType.TRIM: {
    const node: TrimNode = {
      ...baseNode,
      left_node: null,
      left_column: null
    }

    return node
  }
  case NodeType.TEXT_REPLACE: {
    const node: TextReplaceNode = {
      ...baseNode,
      left_node: null,
      left_column: null,
      search_text: '',
      replace_text: ''
    }

    return node
  }
  case NodeType.UNITE: {
    const node: UniteNode = {
      ...baseNode,
      left_node: null,
      right_node: null,
      schema: []
    }

    node.schema.push(Unite.getNewColumn(node))

    return node
  }
  // TODO: more nodes

  default:
    throw new Error(`Unsupported node type: ${type}`)
  }
}

const EDGE_MARKER = {
  type: MarkerType.ArrowClosed,
  width: 16,
  height: 16,
  color: BOARD.signalGreen
}

export function getEdge(connection: Connection): FlowEdge | null {
  if (connection.source && connection.target) {
    return {
      id: `edges-${connection.source}_${connection.target}`,
      type: 'CUSTOM',
      source: connection.source,
      sourceHandle: connection.sourceHandle,
      target: connection.target,
      targetHandle: connection.targetHandle,
      markerEnd: EDGE_MARKER
    }
  }

  return null
}

/**
 * Check if Node has input fields to connect with
 */
function getEdgeKind(node: Node['data']): 'one_child' | 'two_child' | null {
  if ('left_node' in node && !('right_node' in node)) {
    return 'one_child'
  }
  if ('left_node' in node && 'right_node' in node) {
    return 'two_child'
  }

  // no connecting edges
  return null
}

function formatEdge(
  node: OneChildNode | TwoChildNode,
  handle: 'left' | 'right'
): FlowEdge | null {
  const source =
    handle === 'left' ? node.left_node : (node as TwoChildNode).right_node

  if (!source) {
    return null
  }

  return {
    id: `edges-${source}_${node.id}`,
    type: 'CUSTOM',
    source,
    sourceHandle: null,
    target: node.id,
    // set handle for TwoChildNode
    targetHandle: 'right_node' in node ? handle : null,
    markerEnd: EDGE_MARKER
  }
}

export const getInitialElements = (nodes: Node['data'][]) => {
  const flowNodes: Node[] = nodes.map((node) => ({
    id: node.id,
    position: { x: node.location_x, y: node.location_y },
    type: node.type,
    data: node
  }))

  // create connections between nodes
  const edges: FlowEdge[] = nodes.flatMap((node) => {
    const edgeKind = getEdgeKind(node)

    if (edgeKind === 'one_child') {
      const oneChildNode = node as OneChildNode

      return [formatEdge(oneChildNode, 'left')].filter(Boolean) as FlowEdge[]
    } else if (edgeKind === 'two_child') {
      const twoChildNode = node as TwoChildNode

      return [
        formatEdge(twoChildNode, 'left'),
        formatEdge(twoChildNode, 'right')
      ].filter(Boolean) as FlowEdge[]
    }

    return []
  })

  return { edges, nodes: flowNodes }
}

// Need to enforce Partial type for ts to be happy.
type Side = 'left' | 'right'
type NodeWithRefs = NodeData &
  Partial<Record<`${Side}_node` | `${Side}_column`, string | null>>

/**
 * Remove any references to removed nodes.
 * @param node - Node to validate
 * @param removedIds - Node ids that have been removed
 * @param side - Left or Right child node.
 */
export const clearReferences = (
  node: NodeWithRefs,
  removedIds: string[],
  side: Side
) => {
  const ref_node = `${side}_node` as const

  if (
    ref_node in node &&
    node[ref_node] &&
    removedIds.includes(node[ref_node] as string)
  ) {
    node[ref_node] = null
  }
}

/**
 * Remove any references to removed nodes.
 * @param node - Node to validate
 * @param removedIds - Node ids that have been removed
 * @param side - Left or Right child node.
 */
export const updateAffectedNodes = (
  nodes: FlowNode<NodeWithRefs>[],
  removedIds: string[]
) => {
  return nodes.map((n) => {
    if (!removedIds.includes(n.id)) {
      const newData = { ...n.data }

      clearReferences(newData, removedIds, 'left')
      clearReferences(newData, removedIds, 'right')

      return { ...n, data: newData }
    }

    return n
  })
}
